/**
 * Process Engineer surface — root-cause correlation as an investigation
 * workspace, not a chatbot.
 *
 * The chain is visible: EVENT → OBSERVED SIGNALS → HYPOTHESES → SUPPORTING
 * EVIDENCE. Confidence is capped below 0.9 by backend invariants, and every
 * hypothesis says it is a hypothesis.
 */
import { useState } from 'react';
import { api, qs } from '../api/client';
import { useApi, ago } from '../hooks/useApi';
import type { InspectionEvent } from '../api/types';
import { Card, Empty, ErrorBox, Loading, StatePill } from '../components/Metric';

interface Hypothesis {
  id: string;
  hypothesisText: string;
  confidence: number;
  supportingSignals: unknown;
  citedKbChunkIds: unknown;
  telemetryWindow: unknown;
  disclaimer: string | null;
  createdAt: string;
}

interface RootCauseResponse {
  hypotheses?: Hypothesis[];
  disclaimer?: string;
  [key: string]: unknown;
}

export function EngineerPage() {
  const events = useApi<InspectionEvent[]>(
    () => api.get<InspectionEvent[]>(`/api/v1/events${qs({ limit: 40, outcome: 'FLAG_ADVISORY' })}`),
    [],
  );
  const flagged = useApi<InspectionEvent[]>(
    () => api.get<InspectionEvent[]>(`/api/v1/events${qs({ limit: 40 })}`),
    [],
  );

  const [eventId, setEventId] = useState<string>('');
  const [lookback, setLookback] = useState(30);
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<RootCauseResponse | null>(null);

  const list = events.data?.length ? events.data : flagged.data ?? [];
  const selected = list.find((e) => e.id === eventId);

  async function analyse(id: string) {
    setBusy(true);
    setFail(null);
    setAnalysis(null);
    setEventId(id);
    try {
      const result = await api.post<RootCauseResponse>(`/api/v1/events/${id}/root-cause`, {
        lookbackMinutes: lookback,
      });
      setAnalysis(result);
    } catch (err) {
      setFail((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function loadExisting(id: string) {
    setBusy(true);
    setFail(null);
    setEventId(id);
    try {
      setAnalysis(await api.get<RootCauseResponse>(`/api/v1/events/${id}/root-cause`));
    } catch (err) {
      setFail((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="callout advisory">
        <strong>These are hypotheses, not conclusions</strong>
        Root-cause reasoning correlates a defect event with machine telemetry
        from the preceding window and with the domain knowledge base. The
        confidence is deliberately capped below 90%: a causal claim about a
        physical process derived this way is a lead to investigate, not a
        finding to act on blindly.
      </div>

      {fail && <ErrorBox message={fail} />}

      <div className="grid grid-2">
        <Card
          title="Defect events"
          actions={
            <div className="btn-row">
              <label htmlFor="lookback" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                Telemetry lookback
              </label>
              <select
                id="lookback"
                style={{ width: 100 }}
                value={lookback}
                onChange={(e) => setLookback(Number(e.target.value))}
              >
                {[15, 30, 60, 120].map((m) => (
                  <option key={m} value={m}>
                    {m} min
                  </option>
                ))}
              </select>
            </div>
          }
          note="Flagged cycles first, then the most recent cycles. Select one to reason over it."
        >
          {events.loading && <Loading />}
          {list.length === 0 ? (
            <Empty title="No inspection events yet">
              Run some cycles in shadow or active mode first.
            </Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Cycle</th>
                    <th>Result</th>
                    <th className="num">Conf.</th>
                    <th>When</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {list.slice(0, 20).map((e) => (
                    <tr
                      key={e.id}
                      style={eventId === e.id ? { background: 'var(--brand-soft)' } : undefined}
                    >
                      <td className="mono" style={{ fontSize: 11.5 }}>{e.cycleId}</td>
                      <td>
                        <StatePill state={e.outcome} />
                        {e.defectName && <div className="dim">{e.defectName}</div>}
                      </td>
                      <td className="num">{(e.confidence * 100).toFixed(0)}%</td>
                      <td style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{ago(e.createdAt)}</td>
                      <td>
                        <div className="btn-row">
                          <button className="btn sm primary" disabled={busy} onClick={() => void analyse(e.id)}>
                            Analyse
                          </button>
                          <button className="btn sm" disabled={busy} onClick={() => void loadExisting(e.id)}>
                            History
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card
          title="Investigation"
          note={
            analysis?.disclaimer ??
            'Select a defect event and run an analysis. Machine telemetry from the lookback window is correlated with the defect and the knowledge base.'
          }
        >
          {busy && <Loading label="Correlating defect with telemetry…" />}

          {!busy && selected && (
            <div style={{ marginBottom: 12 }}>
              <div className="tag brand">Event</div>
              <div style={{ marginTop: 6, fontSize: 13 }}>
                <span className="mono">{selected.cycleId}</span> ·{' '}
                <StatePill state={selected.outcome} />
                {selected.defectName ? ` · ${selected.defectName}` : ''} ·{' '}
                {(selected.confidence * 100).toFixed(0)}% · {ago(selected.createdAt)}
              </div>
            </div>
          )}

          {!busy && !analysis && !selected && <Empty title="No analysis yet" />}
          {!busy && analysis && (analysis.hypotheses?.length ?? 0) === 0 && (
            <Empty title="No hypotheses produced">
              The model found no defensible correlation in this window. That is
              a valid result — absence of a hypothesis is not an error.
            </Empty>
          )}

          {!busy &&
            analysis?.hypotheses?.map((h, i) => (
              <div
                key={h.id ?? i}
                style={{
                  border: '1px solid var(--border)',
                  borderLeft: '3px solid var(--brand)',
                  borderRadius: 10,
                  padding: 14,
                  marginBottom: 12,
                  background: 'var(--surface-2)',
                }}
              >
                <div className="btn-row" style={{ marginBottom: 8 }}>
                  <span className="tag brand">Hypothesis {i + 1}</span>
                  <span className="pill muted">confidence {(h.confidence * 100).toFixed(0)}%</span>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 'auto' }}>
                    {ago(h.createdAt)}
                  </span>
                </div>
                <p style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 550 }}>{h.hypothesisText}</p>

                {Array.isArray(h.supportingSignals) && h.supportingSignals.length > 0 && (
                  <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
                    <strong style={{ color: 'var(--ink)' }}>Supporting signals</strong>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                      {(h.supportingSignals as string[]).map((s, j) => (
                        <li key={j} className="mono" style={{ fontSize: 11.5, marginBottom: 3 }}>
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {Array.isArray(h.citedKbChunkIds) && h.citedKbChunkIds.length > 0 && (
                  <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 8 }}>
                    Grounded in {h.citedKbChunkIds.length} knowledge-base reference
                    {h.citedKbChunkIds.length === 1 ? '' : 's'}
                  </div>
                )}

                {h.disclaimer && (
                  <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 8, fontStyle: 'italic' }}>
                    {h.disclaimer}
                  </div>
                )}
              </div>
            ))}
        </Card>
      </div>

      <div style={{ marginTop: 14 }}>
        <Card title="How this differs from a rules engine" note="Scope statement.">
          <p style={{ fontSize: 13, color: 'var(--ink-2)' }}>
            A rules engine fires a fixed alarm when a tag crosses a fixed limit.
            It cannot tell you that a rising dew point two hours before a splay
            event points at desiccant saturation, because that relationship lives
            in process knowledge rather than in a threshold. This page reasons
            over both — and then stops short of certainty, because it should.
          </p>
        </Card>
      </div>
    </>
  );
}
